import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { seasonsApi, SeasonMatch } from '../api/seasons';
import { courtsApi } from '../api/courts';
import { pairsApi, Pair } from '../api/pairs';
import { teamsApi, Team } from '../api/teams';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';


const MATCH_STATUS_LABEL: Record<string, string> = {
  pending: 'Offen',
  proposal_sent: 'Terminvorschlag',
  scheduled: 'Terminiert',
  result_pending: 'Ergebnis ausstehend',
  played: 'Gespielt',
  walkover: 'Walkover',
  bye: 'Freilos',
};

const MATCH_STATUS_COLOR: Record<string, string> = {
  pending: 'bg-gray-100 text-gray-500',
  proposal_sent: 'bg-yellow-100 text-yellow-700',
  scheduled: 'bg-blue-100 text-blue-700',
  result_pending: 'bg-orange-100 text-orange-700',
  played: 'bg-green-100 text-green-700',
  walkover: 'bg-red-100 text-red-700',
  bye: 'bg-gray-100 text-gray-400',
};

function MatchCard({ match, myId, myTeamIds, seasonId }: { match: SeasonMatch; myId?: string; myTeamIds: number[]; seasonId: number }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [proposalOpen, setProposalOpen] = useState(false);
  const [resultOpen, setResultOpen] = useState(false);
  const [dt, setDt] = useState('');
  const [courtId, setCourtId] = useState('');
  const [homeSets, setHomeSets] = useState('');
  const [awaySets, setAwaySets] = useState('');
  const [msg, setMsg] = useState('');

  const { data: courts = [] } = useQuery({
    queryKey: ['courts'],
    queryFn: () => courtsApi.list().then(r => r.data),
    enabled: proposalOpen,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['season', seasonId] });

  const proposeMut = useMutation({
    mutationFn: () => seasonsApi.propose(match.id, { scheduled_at: dt, court_id: courtId ? parseInt(courtId) : undefined }),
    onSuccess: () => { invalidate(); setProposalOpen(false); setMsg(''); },
    onError: (e: any) => setMsg(e.response?.data?.detail ?? 'Fehler'),
  });

  const acceptMut = useMutation({
    mutationFn: () => seasonsApi.acceptProposal(match.id),
    onSuccess: () => { invalidate(); setMsg(''); },
    onError: (e: any) => setMsg(e.response?.data?.detail ?? 'Fehler'),
  });

  const resultMut = useMutation({
    mutationFn: () => seasonsApi.submitResult(match.id, { home_sets: parseInt(homeSets), away_sets: parseInt(awaySets) }),
    onSuccess: () => { invalidate(); setResultOpen(false); setMsg(''); },
    onError: (e: any) => setMsg(e.response?.data?.detail ?? 'Fehler'),
  });

  const confirmMut = useMutation({
    mutationFn: () => seasonsApi.confirmResult(match.id),
    onSuccess: () => { invalidate(); setMsg(''); },
    onError: (e: any) => setMsg(e.response?.data?.detail ?? 'Fehler'),
  });

  const rejectMut = useMutation({
    mutationFn: () => seasonsApi.rejectResult(match.id),
    onSuccess: () => { invalidate(); setMsg('Ergebnis abgelehnt – Match wieder offen.'); },
    onError: (e: any) => setMsg(e.response?.data?.detail ?? 'Fehler'),
  });

  const isWettkampfMatch = !!match.home_team;
  const isPairsMatch = !!match.home_pair;

  const homeLabel = isWettkampfMatch
    ? (match.home_team?.name ?? '?')
    : isPairsMatch
      ? (match.home_pair ? `${match.home_pair.player1?.first_name ?? '?'} / ${match.home_pair.player2?.first_name ?? '?'}` : '?')
      : (match.home_user ? `${match.home_user.first_name} ${match.home_user.last_name}` : '?');
  const awayLabel = isWettkampfMatch
    ? (match.away_team?.name ?? '?')
    : isPairsMatch
      ? (match.away_pair ? `${match.away_pair.player1?.first_name ?? '?'} / ${match.away_pair.player2?.first_name ?? '?'}` : '?')
      : (match.away_user ? `${match.away_user.first_name} ${match.away_user.last_name}` : '?');

  const isParticipant = myId && (
    match.home_user?.id === myId ||
    match.away_user?.id === myId ||
    (isPairsMatch && (
      match.home_pair?.player1?.id === myId || match.home_pair?.player2?.id === myId ||
      match.away_pair?.player1?.id === myId || match.away_pair?.player2?.id === myId
    )) ||
    (isWettkampfMatch && myTeamIds.some(tid => tid === match.home_team?.id || tid === match.away_team?.id))
  );
  const isHomeMe = isWettkampfMatch
    ? myTeamIds.some(tid => tid === match.home_team?.id)
    : isPairsMatch
      ? (match.home_pair?.player1?.id === myId || match.home_pair?.player2?.id === myId)
      : match.home_user?.id === myId;

  return (
    <div className={`rounded-xl border p-4 ${match.status === 'played' ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-white'}`}>
      {/* Header */}
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`font-semibold ${isHomeMe ? 'text-primary-700' : 'text-gray-900'}`}>{homeLabel}</span>
            {!isWettkampfMatch && match.home_user && <span className="text-xs text-gray-400">ELO {match.home_user.elo}</span>}
            <span className="text-gray-400 font-medium">vs.</span>
            <span className={`font-semibold ${!isHomeMe && (isWettkampfMatch ? myTeamIds.some(tid => tid === match.away_team?.id) : match.away_user?.id === myId) ? 'text-primary-700' : 'text-gray-900'}`}>{awayLabel}</span>
            {!isWettkampfMatch && match.away_user && <span className="text-xs text-gray-400">ELO {match.away_user.elo}</span>}
          </div>
          {(match.status === 'played' || match.status === 'walkover') && (
            <p className="text-sm mt-1">
              {match.status === 'played' && (
                <span className="font-bold text-gray-900">{match.home_sets} : {match.away_sets} </span>
              )}
              {match.is_walkover && <span className="text-xs text-red-500">Walkover</span>}
              {match.elo_change_home != null && match.elo_change_home !== 0 && (
                <span className="ml-2 text-xs text-gray-500">
                  ({homeLabel}: <span className={match.elo_change_home >= 0 ? 'text-green-600' : 'text-red-500'}>{match.elo_change_home >= 0 ? '+' : ''}{match.elo_change_home}</span>
                  {' / '}{awayLabel}: <span className={match.elo_change_away! >= 0 ? 'text-green-600' : 'text-red-500'}>{match.elo_change_away! >= 0 ? '+' : ''}{match.elo_change_away}</span>)
                </span>
              )}
            </p>
          )}
          {match.scheduled_at && match.status !== 'played' && (
            <p className="text-xs text-gray-500 mt-1">
              📅 {new Date(match.scheduled_at).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' })}
              {match.court && <span className="ml-1">· {match.court.name}, {match.court.city}</span>}
            </p>
          )}
        </div>
        <span className={`badge text-xs ${MATCH_STATUS_COLOR[match.status] ?? ''}`}>
          {MATCH_STATUS_LABEL[match.status] ?? match.status}
        </span>
      </div>

      {msg && <p className="text-red-600 text-xs mt-2">{msg}</p>}

      {/* Actions for participants */}
      {isParticipant && (
        <div className="mt-3 flex gap-2 flex-wrap">
          {/* Propose schedule */}
          {['pending', 'proposal_sent'].includes(match.status) && (
            <button onClick={() => { setProposalOpen(o => !o); setResultOpen(false); }} className="btn btn-secondary btn-sm text-xs">
              {t('season.propose')}
            </button>
          )}
          {/* Accept proposal */}
          {match.status === 'proposal_sent' && match.proposed_by !== myId && (
            <button onClick={() => acceptMut.mutate()} disabled={acceptMut.isPending} className="btn btn-primary btn-sm text-xs">
              {t('season.acceptProposal')}
            </button>
          )}
          {/* Submit result */}
          {['pending','proposal_sent','scheduled'].includes(match.status) && (
            <button onClick={() => { setResultOpen(o => !o); setProposalOpen(false); }} className="btn btn-secondary btn-sm text-xs">
              {t('season.submitResult')}
            </button>
          )}
          {/* Confirm / Reject result */}
          {match.status === 'result_pending' && match.result_submitted_by !== myId && (
            <>
              <span className="text-xs text-gray-500 self-center">
                {match.home_sets}:{match.away_sets} eingetragen –
              </span>
              <button onClick={() => confirmMut.mutate()} disabled={confirmMut.isPending} className="btn btn-primary btn-sm text-xs">
                {t('season.confirmResult')}
              </button>
              <button
                onClick={() => { if (window.confirm('Ergebnis wirklich ablehnen? Das Match wird wieder auf "offen" gesetzt.')) rejectMut.mutate(); }}
                disabled={rejectMut.isPending}
                className="btn btn-secondary btn-sm text-xs border-red-300 text-red-600 hover:bg-red-50">
                Ablehnen
              </button>
            </>
          )}
        </div>
      )}

      {/* Proposal form */}
      {proposalOpen && (
        <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200 space-y-2">
          <label className="label text-xs">{t('season.dateTime')}</label>
          <input type="datetime-local" className="input text-sm" value={dt} onChange={e => setDt(e.target.value)} />
          <label className="label text-xs">{t('season.court')}</label>
          <select className="input text-sm" value={courtId} onChange={e => setCourtId(e.target.value)}>
            <option value="">{t('season.courtOptional')}</option>
            {courts.map(c => <option key={c.id} value={c.id}>{c.name} – {c.city}</option>)}
          </select>
          <button onClick={() => proposeMut.mutate()} disabled={!dt || proposeMut.isPending} className="btn btn-primary btn-sm text-xs">
            {t('season.sendProposal')}
          </button>
        </div>
      )}

      {/* Result form */}
      {resultOpen && (
        <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
          <p className="text-xs text-gray-500 mb-2">{t('season.resultHint')}</p>
          <div className="flex items-center gap-3">
            <div>
              <label className="label text-xs">{homeLabel}</label>
              <input type="number" min={0} max={7} className="input w-16 text-center" value={homeSets} onChange={e => setHomeSets(e.target.value)} />
            </div>
            <span className="text-gray-400 font-bold mt-4">:</span>
            <div>
              <label className="label text-xs">{awayLabel}</label>
              <input type="number" min={0} max={7} className="input w-16 text-center" value={awaySets} onChange={e => setAwaySets(e.target.value)} />
            </div>
            <button onClick={() => resultMut.mutate()} disabled={!homeSets || !awaySets || resultMut.isPending} className="btn btn-primary btn-sm text-xs mt-4">
              {t('season.submit')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function SeasonDetail() {
  const { id } = useParams<{ id: string }>();
  const seasonId = parseInt(id!);
  const { t } = useTranslation();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [actionMsg, setActionMsg] = useState('');
  const [selectedPairId, setSelectedPairId] = useState<number | null>(null);
  const [selectedTeamId, setSelectedTeamId] = useState<number | null>(null);

  const { data: season, isLoading } = useQuery({
    queryKey: ['season', seasonId],
    queryFn: () => seasonsApi.get(seasonId).then(r => r.data),
  });

  const isPairsSeason = season?.season_type === 'pairs';
  const isWettkampfSeason = season?.season_type === 'wettkampf';

  const { data: myPairs = [] } = useQuery({
    queryKey: ['pairs-mine'],
    queryFn: () => pairsApi.listMine().then(r => r.data),
    enabled: !!user && isPairsSeason && season?.status === 'open' && !season?.is_registered,
  });

  const { data: myTeams = [] } = useQuery({
    queryKey: ['teams-mine'],
    queryFn: () => teamsApi.listMine().then(r => r.data),
    enabled: !!user && (isWettkampfSeason || season?.status === 'active'),
  });

  const activePairs = (myPairs as Pair[]).filter(p => p.status === 'active' && p.player2_id);
  const myTeamIds = (myTeams as Team[]).map(t => t.id);

  const registerMut = useMutation({
    mutationFn: () => seasonsApi.register(
      seasonId,
      isPairsSeason ? (selectedPairId ?? undefined) : undefined,
      isWettkampfSeason ? (selectedTeamId ?? undefined) : undefined,
    ),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['season', seasonId] }); qc.invalidateQueries({ queryKey: ['seasons'] }); setActionMsg(''); },
    onError: (e: any) => setActionMsg(e.response?.data?.detail ?? 'Fehler'),
  });

  const withdrawMut = useMutation({
    mutationFn: () => seasonsApi.withdraw(seasonId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['season', seasonId] }); qc.invalidateQueries({ queryKey: ['seasons'] }); setActionMsg(''); },
    onError: (e: any) => setActionMsg(e.response?.data?.detail ?? 'Fehler'),
  });

  if (isLoading || !season) return <div className="text-center py-16">{t('admin.loading')}</div>;

  const myMatches = season.rounds?.flatMap(r => r.matches.filter(m =>
    m.home_user?.id === user?.id ||
    m.away_user?.id === user?.id ||
    m.home_pair?.player1?.id === user?.id ||
    m.home_pair?.player2?.id === user?.id ||
    m.away_pair?.player1?.id === user?.id ||
    m.away_pair?.player2?.id === user?.id ||
    (m.home_team && myTeamIds.includes(m.home_team.id)) ||
    (m.away_team && myTeamIds.includes(m.away_team.id))
  )) ?? [];
  const nextMatch = myMatches.find(m => !['played','walkover'].includes(m.status));

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <Link to="/seasons" className="text-primary-600 hover:text-primary-700 text-sm">{t('season.back')}</Link>
          <h1 className="text-2xl font-bold text-gray-900 mt-1">{season.name}</h1>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <span className="text-sm text-gray-500">{t('season.class')} {season.season_class}</span>
            <span className="text-gray-300">·</span>
            <span className="text-sm text-gray-500">{season.num_rounds} {t('season.rounds')}</span>
            {season.region && <><span className="text-gray-300">·</span><span className="text-sm text-gray-500">📍 {season.region}</span></>}
            {season.elo_min && <span className="text-sm text-gray-500">ELO ≥ {season.elo_min}</span>}
            {season.elo_max && <span className="text-sm text-gray-500">ELO ≤ {season.elo_max}</span>}
          </div>
          {season.description && <p className="text-sm text-gray-600 mt-2 whitespace-pre-line">{season.description}</p>}
        {season.fee != null && (
          <div className={`mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium ${
            season.fee === 0 ? 'bg-green-50 text-green-700' : 'bg-blue-50 text-blue-700'
          }`}>
            {season.fee === 0 ? t('leagueList.free') : `${t('season.feeLabel')}: ${season.fee} €`}
          </div>
        )}
        </div>
      </div>

      {/* Finished banner */}
      {season.status === 'finished' && (() => {
        const regs = [...(season.registrations ?? [])];
        const seenTeams = new Set<number>();
        const deduped = regs.filter(r => {
          if (!r.team) return true;
          if (seenTeams.has(r.team.id)) return false;
          seenTeams.add(r.team.id);
          return true;
        });
        const sorted = deduped.sort((a, b) => b.wins - a.wins || a.losses - b.losses);
        const winner = sorted[0];
        const winnerLabel = winner?.team?.name
          ?? (winner?.pair ? `${winner.pair.player1?.first_name ?? '?'} / ${winner.pair.player2?.first_name ?? '?'}` : winner ? `${winner.user.first_name} ${winner.user.last_name}` : null);
        return (
          <div className="card mb-6 border-2 border-yellow-300 bg-gradient-to-r from-yellow-50 to-amber-50">
            <div className="flex items-center gap-4 flex-wrap">
              <span className="text-4xl">🏆</span>
              <div>
                <p className="text-xs font-semibold text-yellow-700 uppercase tracking-wide mb-0.5">Saison abgeschlossen</p>
                {winnerLabel ? (
                  <p className="text-xl font-bold text-gray-900">{winnerLabel}</p>
                ) : (
                  <p className="text-gray-500 text-sm">Kein Sieger ermittelbar</p>
                )}
                {winner && winnerLabel && (
                  <p className="text-sm text-gray-600 mt-0.5">{winner.wins} Siege · {winner.losses} Niederlagen</p>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Registration card */}
      {user && season.status === 'open' && (() => {
        const isFull = season.max_participants != null &&
          (season.registrations?.length ?? season.participant_count ?? 0) >= season.max_participants;
        return (
        <div className={`card mb-6 border-2 ${season.is_registered ? 'border-green-300 bg-green-50' : isFull ? 'border-red-200 bg-red-50' : 'border-primary-200'}`}>
          {isFull && !season.is_registered ? (
            <div className="flex items-center gap-3">
              <span className="text-2xl">🔒</span>
              <div>
                <p className="font-semibold text-red-700">Saison ist voll</p>
                <p className="text-sm text-gray-500">Die maximale Teilnehmerzahl ({season.max_participants}) wurde erreicht.</p>
              </div>
            </div>
          ) : season.is_registered ? (
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <p className="font-semibold text-green-700">{t('season.alreadyRegistered')}</p>
                {season.fee != null && season.fee > 0 && (
                  <p className="text-sm text-gray-500 mt-0.5">{t('season.feeLabel')}: {season.fee} €</p>
                )}
              </div>
              <button onClick={() => withdrawMut.mutate()} disabled={withdrawMut.isPending}
                className="btn btn-secondary btn-sm border-red-300 text-red-600 hover:bg-red-50">
                {t('season.withdraw')}
              </button>
            </div>
          ) : (
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">{t('season.registerTitle')}</h3>

              {season.fee != null && season.fee > 0 && (
                <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
                  {t('season.feeNotice', { fee: season.fee })}
                </div>
              )}

              {isPairsSeason && (
                <div className="mb-4">
                  <label className="label">{t('season.selectPair')} *</label>
                  {activePairs.length === 0 ? (
                    <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-700">
                      {t('season.noPairs')}{' '}
                      <Link to="/pairs" className="underline font-medium hover:text-yellow-900">
                        {t('season.goToPairs')}
                      </Link>
                    </div>
                  ) : (
                    <select
                      className="input"
                      value={selectedPairId ?? ''}
                      onChange={e => setSelectedPairId(e.target.value ? parseInt(e.target.value) : null)}
                    >
                      <option value="">– {t('season.choosePair')} –</option>
                      {activePairs.map(p => (
                        <option key={p.id} value={p.id}>
                          {p.player1?.first_name} {p.player1?.last_name} / {p.player2?.first_name} {p.player2?.last_name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}

              {isWettkampfSeason && (
                <div className="mb-4">
                  <label className="label">{t('season.selectTeam')} *</label>
                  {(myTeams as Team[]).length === 0 ? (
                    <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-700">
                      {t('season.noTeams')}{' '}
                      <Link to="/teams" className="underline font-medium hover:text-yellow-900">
                        {t('season.goToTeams')}
                      </Link>
                    </div>
                  ) : (
                    <select
                      className="input"
                      value={selectedTeamId ?? ''}
                      onChange={e => setSelectedTeamId(e.target.value ? parseInt(e.target.value) : null)}
                    >
                      <option value="">– {t('season.chooseTeam')} –</option>
                      {(myTeams as Team[]).map(team => (
                        <option key={team.id} value={team.id}>{team.name}</option>
                      ))}
                    </select>
                  )}
                </div>
              )}

              {actionMsg && (
                <p className="text-red-600 text-sm mb-3">{actionMsg}</p>
              )}

              <button
                onClick={() => registerMut.mutate()}
                disabled={
                  registerMut.isPending ||
                  (isPairsSeason && (!selectedPairId || activePairs.length === 0)) ||
                  (isWettkampfSeason && (!selectedTeamId || (myTeams as Team[]).length === 0))
                }
                className="btn btn-primary"
              >
                {registerMut.isPending ? '...' : t('season.register')}
              </button>
            </div>
          )}
        </div>
        );
      })()}

      {/* My next match */}
      {nextMatch && (
        <div className="card border-2 border-primary-200 mb-6">
          <p className="text-xs font-semibold text-primary-600 uppercase tracking-wide mb-2">{t('season.myNextMatch')}</p>
          <MatchCard match={nextMatch} myId={user?.id} myTeamIds={myTeamIds} seasonId={seasonId} />
        </div>
      )}

      {/* Standings */}
      {(season.registrations ?? []).length > 0 && (
        <div className="card mb-6">
          <h2 className="font-semibold text-gray-900 mb-4">{t('season.standings')}</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-2 px-3 text-gray-500 font-medium w-10 border-r border-gray-200">#</th>
                  <th className="text-left py-2 px-3 text-gray-500 font-medium border-r border-gray-200">{t('ranking.player')}</th>
                  <th className="text-center py-2 px-3 text-gray-500 font-medium w-16 border-r border-gray-200">ELO</th>
                  <th className="text-center py-2 px-3 text-green-600 font-medium w-12 border-r border-gray-200">S</th>
                  <th className="text-center py-2 px-3 text-red-500 font-medium w-12">N</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  // Deduplicate by team or pair – show one row per unit
                  const seenTeams = new Set<number>();
                  const seenPairs = new Set<number>();
                  const dedupedRegs = (season.registrations ?? []).filter(reg => {
                    if (reg.team) {
                      if (seenTeams.has(reg.team.id)) return false;
                      seenTeams.add(reg.team.id);
                      return true;
                    }
                    if (reg.pair) {
                      if (seenPairs.has(reg.pair.id)) return false;
                      seenPairs.add(reg.pair.id);
                      return true;
                    }
                    return true;
                  });
                  return [...dedupedRegs].sort((a,b) => b.wins - a.wins || a.losses - b.losses).map((reg, i) => {
                    const isMe = reg.team
                      ? myTeamIds.includes(reg.team.id)
                      : reg.pair
                        ? (reg.pair.player1?.id === user?.id || reg.pair.player2?.id === user?.id)
                        : reg.user.id === user?.id;
                    return (
                      <tr key={reg.id} className={`border-b border-gray-100 last:border-0 ${isMe ? 'bg-primary-50' : ''}`}>
                        <td className="py-2 px-3 text-gray-400 border-r border-gray-200">{i+1}</td>
                        <td className="py-2 px-3 border-r border-gray-200">
                          {reg.team ? (
                            <div>
                              <span className="font-medium text-gray-900">{reg.team.name}</span>
                              {reg.team.members && reg.team.members.length > 0 && (
                                <div className="text-xs text-gray-500 mt-0.5">
                                  {reg.team.members.map(m => `${m.first_name} ${m.last_name} (${m.elo})`).join(', ')}
                                </div>
                              )}
                            </div>
                          ) : reg.pair ? (
                            <span className="font-medium text-gray-900">
                              <Link to={`/users/${reg.pair.player1?.id ?? ''}`} className="hover:text-primary-600">
                                {reg.pair.player1?.first_name} {reg.pair.player1?.last_name}
                              </Link>
                              <span className="text-gray-300 text-xs ml-1">({reg.pair.player1?.elo})</span>
                              <span className="text-gray-400 mx-1">/</span>
                              <Link to={`/users/${reg.pair.player2?.id ?? ''}`} className="hover:text-primary-600">
                                {reg.pair.player2?.first_name} {reg.pair.player2?.last_name}
                              </Link>
                              <span className="text-gray-300 text-xs ml-1">({reg.pair.player2?.elo})</span>
                            </span>
                          ) : (
                            <Link to={`/users/${reg.user.id}`} className="font-medium text-gray-900 hover:text-primary-600">
                              {reg.user.first_name} {reg.user.last_name}
                            </Link>
                          )}
                        </td>
                        <td className="py-2 px-3 text-center text-gray-700 border-r border-gray-200">
                          {reg.pair
                            ? `⌀ ${reg.pair.current_elo ?? Math.round(((reg.pair.player1?.elo ?? 0) + (reg.pair.player2?.elo ?? 0)) / 2)}`
                            : reg.team
                              ? `⌀ ${reg.elo_at_registration}`
                              : reg.elo_at_registration}
                        </td>
                        <td className="py-2 px-3 text-center font-bold text-green-600 border-r border-gray-200">{reg.wins}</td>
                        <td className="py-2 px-3 text-center font-bold text-red-500">{reg.losses}</td>
                      </tr>
                    );
                  });
                })()}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Rounds */}
      {(season.rounds ?? []).map(round => (
        <div key={round.id} className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-gray-900">
              {t('season.round')} {round.round_number}
            </h2>
            <span className="text-xs text-gray-400">
              {t('season.deadline')}: {new Date(round.deadline).toLocaleDateString('de-DE')}
            </span>
          </div>
          <div className="space-y-3">
            {round.matches.map(m => (
              <MatchCard key={m.id} match={m} myId={user?.id} myTeamIds={myTeamIds} seasonId={seasonId} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
